import * as client from "_graphql-types";
import * as types from "./types";

type Sdk = ReturnType<typeof client.getSdk>;

/*********************************************************************/
// Data Provider Functions
/*********************************************************************/

async function getList(
  sdk: Sdk,
  params: {
    filter?: {
      investmentId?: number;
    };
  }
): types.GetListOut<any> {
  const investmentId = Number(params.filter?.investmentId);
  if (!investmentId) {
    return { data: [], total: 0 };
  }

  // Fetch from investment query which includes fundsXProxies field
  const { investment } = await sdk.getOneInvestment({ id: investmentId });

  const data = investment?.fundsXProxies || [];
  const total = data.length;
  return { data, total };
}

/*********************************************************************/

async function getOne(
  sdk: Sdk,
  params: types.GetOneParams
): types.GetOneOut<any> {
  const id = Number(params.id);

  // @ts-ignore - GraphQL types need to be regenerated after schema changes
  const { fundsXProxies } = await sdk.getOneFundsXProxies({ id });

  if (!fundsXProxies) {
    throw new Error(`FundsXProxies with id ${id} not found`);
  }

  return { data: fundsXProxies };
}

/*********************************************************************/

async function create(sdk: Sdk, params: types.CreateParams<any>) {
  const { data } = params;

  const input: any = {
    investmentId: types.required(data, "investmentId"),
    proxyId: data.proxy?.id || types.required(data, "proxyId"),
    proxyScalingFactor: data.proxyScalingFactor,
    rank: types.required(data, "rank"),
  };

  // @ts-ignore - GraphQL types need to be regenerated after schema changes
  const { addFundsXProxies } = await sdk.createFundsXProxies({
    input,
  });

  return { data: addFundsXProxies };
}

/*********************************************************************/

async function update(
  sdk: Sdk,
  params: types.UpdateParams<any>
): types.UpdateOut<any> {
  const id = Number(params.id);
  const { data } = params;

  const input: any = {};

  if (data.proxy) {
    input.proxyId = data.proxy.id;
  } else if (data.proxyId !== undefined) {
    input.proxyId = data.proxyId;
  }

  if (data.proxyScalingFactor !== undefined) {
    input.proxyScalingFactor = data.proxyScalingFactor;
  }

  if (data.rank !== undefined) {
    input.rank = data.rank;
  }

  // @ts-ignore - GraphQL types need to be regenerated after schema changes
  const { updateFundsXProxies } = await sdk.updateFundsXProxies({
    id,
    input,
  });

  return { data: updateFundsXProxies };
}

/*********************************************************************/

async function del(sdk: Sdk, params: types.DeleteParams): types.DeleteOut<any> {
  const id = Number(params.id);

  // @ts-ignore - GraphQL types need to be regenerated after schema changes
  await sdk.deleteFundsXProxies({ id });

  return { data: { id } };
}

/*********************************************************************/

export const fundsXProxies = types.dataProvider({
  getList,
  getOne,
  create,
  update,
  delete: del,
});
